import { useState } from 'react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { SearchInput } from '../../component-library/shared/SearchInput'
import { useGetGlossaryMarkdown } from './useGetGlossary'
import { useAppContext } from '@/catalog-apps/hooks/useAppContext'
import { useMdTextSearch } from '../../component-library/MdTextComponent/useMdTextSearch'
import { MdTextSearchNavigator } from '../../component-library/MdTextComponent/MdTextSearchNavigator'
import { MermaidRenderer } from '../../component-library/MdTextComponent/MermaidRenderer'
import { EmptyState } from '@/components/ui/EmptyState'

export function GlossaryV2() {
  const { appId } = useAppContext()
  const { data } = useGetGlossaryMarkdown(appId)
  const content = data?.content ?? ''
  const isContentEmpty = content.trim() === ''
  const [searchValue, setSearchValue] = useState('')

  const { contentRef, hasSearchTerm, matchCount, activeMatchIndex, goToPreviousMatch, goToNextMatch } = useMdTextSearch(
    {
      searchValue,
      isEditing: false,
      content,
    }
  )

  return (
    <div className="flex flex-col gap-2">
      <div className="sticky top-0 z-10 flex w-full items-center justify-between gap-2 bg-background/50 py-2 backdrop-blur">
        {!isContentEmpty && <SearchInput value={searchValue} onChange={setSearchValue} />}
      </div>

      {isContentEmpty ? (
        <EmptyState
          className="min-h-80"
          title="No glossary content yet"
          description="A glossary has not been published for this application yet."
        />
      ) : (
      <div className="relative">
        <MdTextSearchNavigator
          hasSearchTerm={hasSearchTerm}
          matchCount={matchCount}
          activeMatchIndex={activeMatchIndex}
          onPrevious={goToPreviousMatch}
          onNext={goToNextMatch}
          className="sticky right-auto top-14 z-20 mb-2 ml-auto w-fit"
        />
        <div ref={contentRef} className="prose prose-sm max-w-none dark:prose-invert">
          <Markdown
            remarkPlugins={[remarkGfm]}
            components={{
              pre({ children, ...props }) {
                const codeElement = children as React.ReactElement
                if (codeElement?.props?.className?.includes('language-mermaid')) {
                  return <MermaidRenderer chart={String(codeElement.props.children).replace(/\n$/, '')} />
                }
                return <pre {...props}>{children}</pre>
              },
              code({ className, children, ...props }) {
                return (
                  <code className={className} {...props}>
                    {children}
                  </code>
                )
              },
            }}
          >
            {content}
          </Markdown>
        </div>
      </div>
      )}
    </div>
  )
}
